import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiRuntimeConfig } from './api-runtime-config';

export interface MockRuntimeConfig {
  defaultClaimLeaseSeconds: number;
  resumeWorkAfterLogout: boolean;
  maxOpenWorkItems: number;
}

/**
 * Loads tunable mock-server settings (`GET /api/ops/mock-config`) after login.
 * The route is mock-only, so `dev`/`prod` keep the defaults below.
 */
@Injectable({ providedIn: 'root' })
export class MockRuntimeConfigService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiRuntimeConfig);

  private readonly config = signal<MockRuntimeConfig>({
    defaultClaimLeaseSeconds: 300,
    resumeWorkAfterLogout: false,
    maxOpenWorkItems: environment.features.maxOpenWorkTabs,
  });

  readonly snapshot = this.config.asReadonly();

  resumeWorkAfterLogout(): boolean {
    return this.config().resumeWorkAfterLogout;
  }

  async refresh(): Promise<void> {
    if (!this.apiConfig.mockApiAvailable()) {
      return;
    }
    try {
      const body = await firstValueFrom(
        this.http.get<MockRuntimeConfig>(`${environment.apiBaseUrl}/ops/mock-config`),
      );
      this.config.set({
        defaultClaimLeaseSeconds: body.defaultClaimLeaseSeconds ?? 300,
        resumeWorkAfterLogout: body.resumeWorkAfterLogout === true,
        maxOpenWorkItems: body.maxOpenWorkItems ?? environment.features.maxOpenWorkTabs,
      });
    } catch {
      // Keep defaults — production APIs may not expose this mock-only route.
    }
  }
}
